import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { z } from 'zod';

interface UseQueryParamsConfig<T extends z.ZodTypeAny> {
  schema: T;
  defaultValues: z.infer<T>;
  pushState?: boolean;
}

export interface IResponseQuery<T extends z.ZodTypeAny> {
  queryParams: z.infer<T>;
  setQueryParams: (newParams: Partial<z.infer<T>>, cb?: () => void) => void;
}

export function useNextQueryParams<T extends z.ZodTypeAny>(
  config: UseQueryParamsConfig<T>
): IResponseQuery<T> {
  const router = useRouter();

  const parsedQuery = config.schema.safeParse({
    ...config.defaultValues,
    ...router.query,
  });

  const initialValues = parsedQuery.success ? parsedQuery.data : config.defaultValues;
  const [queryParams, setQueryParamsState] = useState<T>(initialValues);

  useEffect(() => {
    if (!router.isReady) return;

    if (!Object.keys(router.query).length) {
      // If query is empty, maybe reset to default? But only if we want that behavior.
      // Current logic: router.query is empty -> set default.
      // But queryParams might have local state.
      // Let's stick to syncing one way: Router -> State.

      // If we want two-way sync, it's more complex.
      // Here it seems we just want to reset if URL is clean.
      setQueryParamsState(config.defaultValues);
      return;
    }

    const query = { ...config.defaultValues, ...router.query };
    const result = config.schema.safeParse(query);

    if (result.success) {
      setQueryParamsState(result.data);
    }
  }, [router.isReady, router.query, config.defaultValues, config.schema]);

  const setQueryParams = (newParams: Partial<z.infer<T>>, callback?: () => void) => {
    const mergedQueryParams = {
      ...queryParams,
      ...newParams,
    };

    // Remove undefined/null values to clean up URL
    Object.keys(mergedQueryParams).forEach(
      (key) => mergedQueryParams[key] === undefined && delete mergedQueryParams[key]
    );

    const routerMethod = config.pushState ? router.push : router.replace;

    routerMethod(
      {
        pathname: router.pathname,
        query: mergedQueryParams,
      },
      undefined,
      { shallow: true }
    ).then(() => {
      if (callback) callback();
    });
  };

  return {
    queryParams,
    setQueryParams,
  };
}
